/**
 * Thin client for Google Places API (New) autocomplete + place details, and
 * the Address Validation API. Called directly from the browser with a
 * referrer-restricted API key (VITE_GOOGLE_MAPS_API_KEY).
 *
 * All functions degrade gracefully: if the key is missing or a request fails,
 * the form must keep working as a plain manual-entry form.
 */

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

export const placesEnabled = Boolean(API_KEY);

export interface AddressSuggestion {
  placeId: string;
  /** Full human-readable suggestion, e.g. "65 Clyde Rd, Somerset, NJ, USA" */
  text: string;
}

/** Address parsed into the ship-request form's field shape. */
export interface ResolvedAddress {
  address1: string;
  /** Only present when Google returns a subpremise (suite / unit). */
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
  /** Mapped onto the form's country option values ("UNITED STATES", "Canada", …). */
  country: string;
}

const COUNTRY_OPTION_MAP: Record<string, string> = {
  'United States': 'UNITED STATES',
  Canada: 'Canada',
  Mexico: 'Mexico',
  Japan: 'Japan',
  'Costa Rica': 'Costa Rica',
  'Puerto Rico': 'Puerto Rico',
};

/** ISO region codes the Address Validation API reliably covers for our ship-to countries. */
const VALIDATABLE_REGIONS: Record<string, string> = {
  'UNITED STATES': 'US',
  'Puerto Rico': 'PR',
  Canada: 'CA',
  Mexico: 'MX',
};

export async function autocompleteAddress(
  input: string,
  sessionToken: string,
): Promise<AddressSuggestion[]> {
  if (!API_KEY) return [];

  const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': API_KEY,
    },
    body: JSON.stringify({
      input,
      sessionToken,
      // Bias toward street-level addresses rather than businesses/regions
      includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'route'],
    }),
  });

  if (!res.ok) return [];

  const data = (await res.json()) as {
    suggestions?: { placePrediction?: { placeId?: string; text?: { text?: string } } }[];
  };

  return (data.suggestions ?? [])
    .map((s) => ({
      placeId: s.placePrediction?.placeId ?? '',
      text: s.placePrediction?.text?.text ?? '',
    }))
    .filter((s) => s.placeId && s.text);
}

interface AddressComponent {
  longText?: string;
  shortText?: string;
  types?: string[];
}

function component(components: AddressComponent[], type: string): AddressComponent | undefined {
  return components.find((c) => c.types?.includes(type));
}

export async function fetchPlaceAddress(
  placeId: string,
  sessionToken: string,
): Promise<ResolvedAddress | null> {
  if (!API_KEY) return null;

  const url = new URL(`https://places.googleapis.com/v1/places/${placeId}`);
  url.searchParams.set('sessionToken', sessionToken);

  const res = await fetch(url.toString(), {
    headers: {
      'X-Goog-Api-Key': API_KEY,
      'X-Goog-FieldMask': 'addressComponents',
    },
  });

  if (!res.ok) return null;

  const data = (await res.json()) as { addressComponents?: AddressComponent[] };
  const comps = data.addressComponents ?? [];

  const streetNumber = component(comps, 'street_number')?.longText ?? '';
  const route = component(comps, 'route')?.longText ?? '';
  const subpremise = component(comps, 'subpremise')?.longText;
  const city =
    component(comps, 'locality')?.longText ??
    component(comps, 'postal_town')?.longText ??
    component(comps, 'sublocality_level_1')?.longText ??
    component(comps, 'administrative_area_level_2')?.longText ??
    '';
  const state = component(comps, 'administrative_area_level_1')?.shortText ?? '';
  const zipCode = component(comps, 'postal_code')?.longText ?? '';
  const countryName = component(comps, 'country')?.longText ?? '';

  return {
    address1: [streetNumber, route].filter(Boolean).join(' '),
    address2: subpremise ? `Suite ${subpremise}` : undefined,
    city,
    state,
    zipCode,
    country: COUNTRY_OPTION_MAP[countryName] ?? 'Other',
  };
}

export interface AddressValidationResult {
  /** 'verified' | 'unverified' | 'skipped' (unsupported country, missing key, or API error) */
  status: 'verified' | 'unverified' | 'skipped';
  /** Google's cleaned-up version of the address, when it differs. */
  formattedAddress?: string;
  /** Google's corrected address parsed into form fields, for one-click apply. */
  suggested?: ResolvedAddress;
  /** Friendly names of the parts Google couldn't confirm (e.g. "street name"). */
  unconfirmed?: string[];
  /**
   * Friendly names of the parts Google silently changed from what was typed
   * (e.g. "state" when "NJJ" came back as "NJ"). These verify cleanly at the
   * verdict level, so without this they used to submit as entered.
   */
  corrected?: string[];
}

const COMPONENT_FRIENDLY_NAMES: Record<string, string> = {
  street_number: 'street number',
  route: 'street name',
  subpremise: 'suite/unit',
  locality: 'city',
  administrative_area_level_1: 'state',
  postal_code: 'ZIP code',
  country: 'country',
};

const REGION_TO_COUNTRY_OPTION: Record<string, string> = Object.fromEntries(
  Object.entries(VALIDATABLE_REGIONS).map(([option, region]) => [region, option]),
);

/* US state / territory names, spaces stripped, → USPS abbreviation. Google
   answers with the abbreviation, so someone typing "New Jersey" in full must not
   be told their address was corrected. Only consulted for US/PR. */
const US_STATE_ABBR: Record<string, string> = {
  ALABAMA: 'AL', ALASKA: 'AK', ARIZONA: 'AZ', ARKANSAS: 'AR', CALIFORNIA: 'CA',
  COLORADO: 'CO', CONNECTICUT: 'CT', DELAWARE: 'DE', DISTRICTOFCOLUMBIA: 'DC',
  FLORIDA: 'FL', GEORGIA: 'GA', HAWAII: 'HI', IDAHO: 'ID', ILLINOIS: 'IL',
  INDIANA: 'IN', IOWA: 'IA', KANSAS: 'KS', KENTUCKY: 'KY', LOUISIANA: 'LA',
  MAINE: 'ME', MARYLAND: 'MD', MASSACHUSETTS: 'MA', MICHIGAN: 'MI',
  MINNESOTA: 'MN', MISSISSIPPI: 'MS', MISSOURI: 'MO', MONTANA: 'MT',
  NEBRASKA: 'NE', NEVADA: 'NV', NEWHAMPSHIRE: 'NH', NEWJERSEY: 'NJ',
  NEWMEXICO: 'NM', NEWYORK: 'NY', NORTHCAROLINA: 'NC', NORTHDAKOTA: 'ND',
  OHIO: 'OH', OKLAHOMA: 'OK', OREGON: 'OR', PENNSYLVANIA: 'PA',
  RHODEISLAND: 'RI', SOUTHCAROLINA: 'SC', SOUTHDAKOTA: 'SD', TENNESSEE: 'TN',
  TEXAS: 'TX', UTAH: 'UT', VERMONT: 'VT', VIRGINIA: 'VA', WASHINGTON: 'WA',
  WESTVIRGINIA: 'WV', WISCONSIN: 'WI', WYOMING: 'WY',
  PUERTORICO: 'PR', GUAM: 'GU', VIRGINISLANDS: 'VI', AMERICANSAMOA: 'AS',
};

const lettersOnly = (v: string) => v.trim().toUpperCase().replace(/[^A-Z]/g, '');

/** True when what the requester typed means the same state Google returned. */
function statesAgree(entered: string, returned: string, regionCode: string): boolean {
  const e = lettersOnly(entered);
  const g = lettersOnly(returned);
  if (!g || !e) return true;               // nothing to compare
  if (e === g) return true;
  // Full name typed against the abbreviation Google answers with.
  if (regionCode === 'US' || regionCode === 'PR') return US_STATE_ABBR[e] === g;
  // Other regions: don't guess at local naming, rely on Google's own flags.
  return true;
}

/* ZIP+4 vs 5-digit is not a correction, so compare the 5-digit core. Falls back
   to an alphanumeric key so "M5V 3L9" matches "M5V3L9" for Canada. */
const zipKey = (v: string) => v.match(/\d{5}/)?.[0] ?? v.toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * Checks whether a free-text address (e.g. an autocomplete prediction) verifies
 * to a real premise. Used to filter "did you mean" candidates so we never
 * suggest an extrapolated street number that doesn't actually exist.
 */
export async function isRealPremise(addressText: string, countryOption: string): Promise<boolean> {
  const regionCode = VALIDATABLE_REGIONS[countryOption];
  if (!API_KEY || !regionCode) return false;
  try {
    const res = await fetch(
      `https://addressvalidation.googleapis.com/v1:validateAddress?key=${API_KEY}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: { regionCode, addressLines: [addressText] } }),
      },
    );
    if (!res.ok) return false;
    const data = (await res.json()) as {
      result?: { verdict?: { validationGranularity?: string; hasUnconfirmedComponents?: boolean } };
    };
    const verdict = data.result?.verdict;
    const granularity = verdict?.validationGranularity ?? 'OTHER';
    return (
      !verdict?.hasUnconfirmedComponents &&
      (granularity === 'PREMISE' || granularity === 'SUB_PREMISE')
    );
  } catch {
    return false;
  }
}

export async function validateShippingAddress(addr: {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}): Promise<AddressValidationResult> {
  const regionCode = VALIDATABLE_REGIONS[addr.country];
  if (!API_KEY || !regionCode) return { status: 'skipped' };

  try {
    const res = await fetch(
      `https://addressvalidation.googleapis.com/v1:validateAddress?key=${API_KEY}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: {
            regionCode,
            administrativeArea: addr.state,
            locality: addr.city,
            postalCode: addr.zipCode,
            addressLines: [addr.address1, addr.address2].filter(Boolean),
          },
        }),
      },
    );

    if (!res.ok) return { status: 'skipped' };

    const data = (await res.json()) as {
      result?: {
        verdict?: {
          addressComplete?: boolean;
          hasUnconfirmedComponents?: boolean;
          hasReplacedComponents?: boolean;
          validationGranularity?: string;
        };
        address?: {
          formattedAddress?: string;
          postalAddress?: {
            regionCode?: string;
            administrativeArea?: string;
            locality?: string;
            postalCode?: string;
            addressLines?: string[];
          };
          addressComponents?: {
            componentType?: string;
            confirmationLevel?: string;
            /* Google reports a fixed-up component HERE, not in the verdict flags. */
            spellCorrected?: boolean;
            replaced?: boolean;
            inferred?: boolean;
          }[];
        };
      };
    };

    // `addressComplete` is unreliable (true for fake-but-well-formed addresses,
    // absent for real ones with inferred components). Granularity + unconfirmed
    // components is the signal that actually separates real from junk. Replaced
    // components (e.g. Google swapped in the right zip) also block auto-pass —
    // otherwise we'd silently submit the user's wrong value.
    const verdict = data.result?.verdict;
    const granularity = verdict?.validationGranularity ?? 'OTHER';
    const components = data.result?.address?.addressComponents ?? [];
    const postalCheck = data.result?.address?.postalAddress;

    /* A misspelled state used to sail straight through. Google quietly fixes it
       and reports success: "NJJ" and even a wrong-but-real "NY" come back as
       PREMISE granularity with hasReplacedComponents ABSENT — the correction is
       flagged only on the component (spellCorrected/replaced). So read the
       components, not just the verdict.

       And Google does not always flag it at all: "New Jersy" returns "NJ" with
       spellCorrected false. Hence the value comparison below as well. Neither
       check alone catches both. */
    const CORRECTABLE = new Set([
      'administrative_area_level_1', 'locality', 'postal_code', 'route',
      'street_number', 'subpremise', 'country',
    ]);
    const correctedByGoogle = components
      .filter((c) => CORRECTABLE.has(c.componentType ?? '') && (c.spellCorrected || c.replaced))
      .map((c) => COMPONENT_FRIENDLY_NAMES[c.componentType ?? ''] ?? (c.componentType ?? '').replace(/_/g, ' '));

    const correctedByValue: string[] = [];
    if (postalCheck?.administrativeArea &&
        !statesAgree(addr.state, postalCheck.administrativeArea, regionCode)) {
      correctedByValue.push(COMPONENT_FRIENDLY_NAMES.administrative_area_level_1);
    }
    if (postalCheck?.postalCode && zipKey(addr.zipCode) !== zipKey(postalCheck.postalCode)) {
      correctedByValue.push(COMPONENT_FRIENDLY_NAMES.postal_code);
    }

    const corrected = [...correctedByGoogle, ...correctedByValue]
      .filter((name, i, arr) => name && arr.indexOf(name) === i);

    const verified =
      !verdict?.hasUnconfirmedComponents &&
      !verdict?.hasReplacedComponents &&
      corrected.length === 0 &&
      (granularity === 'PREMISE' || granularity === 'SUB_PREMISE');

    // Only offer Google's returned address as a suggestion when every component
    // was confirmed — with unconfirmed components it's just the junk input
    // echoed back with cosmetic formatting, not a real correction.
    const postal = data.result?.address?.postalAddress;
    const suggested: ResolvedAddress | undefined =
      !verdict?.hasUnconfirmedComponents && postal?.addressLines?.[0]
      ? {
          address1: postal.addressLines[0],
          address2: postal.addressLines[1] || undefined,
          city: postal.locality ?? '',
          state: postal.administrativeArea ?? '',
          zipCode: postal.postalCode ?? '',
          country: REGION_TO_COUNTRY_OPTION[postal.regionCode ?? ''] ?? addr.country,
        }
      : undefined;

    const unconfirmed = (data.result?.address?.addressComponents ?? [])
      .filter((c) => c.confirmationLevel && c.confirmationLevel !== 'CONFIRMED')
      .map((c) => COMPONENT_FRIENDLY_NAMES[c.componentType ?? ''] ?? (c.componentType ?? '').replace(/_/g, ' '))
      .filter((name, i, arr) => name && arr.indexOf(name) === i);

    return {
      status: verified ? 'verified' : 'unverified',
      formattedAddress: data.result?.address?.formattedAddress,
      suggested,
      unconfirmed,
      corrected,
    };
  } catch {
    return { status: 'skipped' };
  }
}
